//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import {
  IAugmentSwipeUpLinkRequest,
  IAugmentSwipeUpLinkResponse,
  IGetDestinationUrlRequest,
  IGetDestinationUrlResponse,
  IGetDestinationUrlsRequest,
  IGetDestinationUrlsResponse,
  IShortenUrlRequest,
  IShortenUrlResponse,
  ITrackingShortLinkBody,
} from '@apis/url-tracking/urlTrackingApiModel';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const instagramGatewayUrlTrackingFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `/api/v1/url-tracking${endpoint}`,
    init,
  );
};

const webGatewayUrlTrackingFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v1/url-tracking/short-link`,
    init,
  );
};

// TODO point this function to use getTrackingShortLink, or replace with getTrackingShortLink completely
/**
 * Augment a story swipe up link with tracking params and shorten it. This should only be used for IG story links
 *
 * /api/v1/url-tracking/augment-swipe-up-link
 *
 * @param request contains the swipe up link to be augmented and the cuid for the story media
 */
export async function getAugmentedSwipeUpLink(
  request: IAugmentSwipeUpLinkRequest,
): Promise<string> {
  const res = await instagramGatewayUrlTrackingFetch('/augment-swipe-up-link', {
    body: JSON.stringify(request),
    method: 'POST',
  });

  const response = await res.json<IAugmentSwipeUpLinkResponse>();

  return response.data;
}

/**
 * Shorten any url into a Curalate shortlink
 *
 * /api/v1/url-tracking/shorten-url
 *
 * @param request contains the url to be augmented, an optional cuid, and an optional metrics SourceType
 */
export async function getShortlink(
  request: IShortenUrlRequest,
): Promise<string> {
  const res = await instagramGatewayUrlTrackingFetch('/shorten-url', {
    body: JSON.stringify(request),
    method: 'POST',
  });

  const response = await res.json<IShortenUrlResponse>();

  return response.data;
}
// TODO change this function to use web gateway's /api/v1/url-tracking/short-link as part of PD-195216
/**
 * Shorten any url into a Curalate shortlink, adds tracking parameters to the destination url
 *
 * /api/v1/url-tracking/short-link
 *
 * @param body contains the url to be paramaterized,
 * a SourceType to determine how the link should be parameterized
 * an optional reserveId:
 * the reserve id is the unique identifier added to the reserve tracking param so that data related to the link
 * can be separated from other links using the same base reserve param,
 * for example Instagram Stories the cuid is used as the reserveId. Some post types like FB posts generate their own unique id and do not need
 * one sent as part of the request.
 * @returns the shortened url pointing to the parameterized link
 */
export async function getTrackingShortLink(
  body: ITrackingShortLinkBody,
): Promise<string> {
  const res = await webGatewayUrlTrackingFetch('/short-link', {
    body: JSON.stringify(body),
    method: 'POST',
  });
  const response = await res.json<IShortenUrlResponse>();
  return response.data;
}

/**
 * Retrieve the full destination url for a Curalate shortlink
 *
 * /api/v1/url-tracking/destination-url
 *
 * @param request contains the shortlink to retrieve a destination url for
 */
export async function getDestinationUrlFromShortlink(
  request: IGetDestinationUrlRequest,
): Promise<string | undefined> {
  const res = await instagramGatewayUrlTrackingFetch('/destination-url', {
    body: JSON.stringify(request),
    method: 'POST',
  });

  const response = await res.json<IGetDestinationUrlResponse>();

  return response.data;
}

/**
 * Gets the destination urls for a list of links and returns them in a Map from link to destination url
 *
 * /api/v1/url-tracking/destination-urls
 */
export async function getDestinationUrlsFromShortlinks(
  request: IGetDestinationUrlsRequest,
): Promise<Record<string, string>> {
  const res = await instagramGatewayUrlTrackingFetch('/destination-urls', {
    body: JSON.stringify(request),
    method: 'POST',
  });

  const response = await res.json<IGetDestinationUrlsResponse>();

  return response.data;
}
